import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import isEqual from 'lodash/isEqual';

import './RpmDataModal.css';

import api from '../../../../../../services/api';
import HereService from '../../../../../../services/Here';
import constants from '../../../../../../constants';

import AirTabbedModal from '../../../../../../common/components/AirTabbedModal';
import RpmDataTable from './RpmDataTable';
import RpmModalHeader from './RpmModalHeader';
import RpmModalGraphHeader from './RpmModalGraphHeader';
import RpmModalSummary from './RpmModalSummary';
import RpmTabChart from './RpmTabChart';

const API_KEY = constants.HERE_API_KEY;

@inject('translationsStore', 'commonStore', 'noticesStore')
@observer
export default class RpmDataModal extends Component {
  constructor(props) {
    super(props);
    this.mapservice = new HereService(API_KEY);
    this.state = {
      loadingRpmData: true,
      visibleTabbed: this.props.visible,
      rpms: {},
      rpmsGraph: {},
      enrichedWithAddresses: null,
      startDate: moment()
        .subtract(6, 'days')
        .startOf('day')
        .toDate(),
      endDate: moment()
        .startOf('day')
        .toDate(),
      startDateGraph: moment()
        .subtract(6, 'days')
        .startOf('day')
        .toDate(),
      endDateGraph: moment()
        .startOf('day')
        .toDate(),
      overRpmThreshold: undefined
    };
  }

  componentDidMount() {
    if (this.props.carId && this.state.loadingRpmData === true) {
      this.initRpmData(this.props.carId, this.state.startDate, this.state.endDate);
      this.initRpmDataGraph(this.props.carId, this.state.startDateGraph, this.state.endDateGraph);
      this.setState({ loadingRpmData: false });
    }
    const { carId, visible } = this.props;
    if (carId !== undefined && visible) {
      this.onRpmDataAvailable();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.carId && this.props.carId !== prevProps.carId) {
      this.initRpmData(this.props.carId, this.state.startDate, this.state.endDate);
      this.initRpmDataGraph(this.props.carId, this.state.startDateGraph, this.state.endDateGraph);
      this.setState({ loadingRpmData: false });
    }
    if (this.state.startDate !== prevState.startDate || this.state.endDate !== prevState.endDate) {
      this.initRpmData(this.props.carId, this.state.startDate, this.state.endDate);
    }
    if (this.state.startDateGraph !== prevState.startDateGraph || this.state.endDateGraph !== prevState.endDateGraph) {
      this.initRpmDataGraph(this.props.carId, this.state.startDateGraph, this.state.endDateGraph);
    }
    const { carId, visible } = this.props;
    if (carId !== undefined && visible) {
      if (carId !== prevProps.carId || visible !== prevProps.visible) {
        this.onRpmDataAvailable();
      }
    }
  }

  onRpmDataAvailable = () => {
    const { noticesStore } = this.props;
    const { plate } = this.props.car;
    noticesStore.setMultipleDismissedSamePlateType({ plate, type: 'rpm-over' });
  };

  initRpmData(carId, startDate, endDate) {
    return api('getRpm', this.props.carId, moment(startDate).format('YYYY-MM-DD'), moment(endDate).format('YYYY-MM-DD'))
      .then(rpms => {
        this.loadingRpmData = false;
        return rpms;
      })
      .then(rpms => {
        const collection = rpms.rpms.sort((a, b) => {
          return b.startDateTime > a.startDateTime ? 1 : -1;
        });
        return { ...rpms, rpms: collection };
      })
      .then(this.enrich)
      .then(rpms => {
        const { rpms: dataSet } = rpms;
        const { overRpmThreshold } = dataSet.slice().shift();
        this.setState({ rpms, overRpmThreshold });
      })
      .catch(() => {
        this.setState({ rpms: { rpms: [] }, overRpmThreshold: undefined });
      });
  }

  initRpmDataGraph(carId, startDateGraph, endDateGraph) {
    return api('getRpm', this.props.carId, moment(startDateGraph).format('YYYY-MM-DD'), moment(endDateGraph).format('YYYY-MM-DD'))
      .then(rpms => {
        this.loadingRpmData = false;
        return rpms;
      })
      .then(rpms => {
        const collection = rpms.rpms.sort((a, b) => {
          return b.startDateTime > a.startDateTime ? 1 : -1;
        });
        return { ...rpms, rpms: collection };
      })
      .then(this.enrich)
      .then(rpms => {
        this.setState({ rpmsGraph: rpms });
      })
      .catch(() => {
        this.setState({ rpmsGraph: { rpms: [] } });
      });
  }

  enrich = rpms => {
    let addresses = [];

    const reverseGeocodeCalls = rpms.rpms.map(aValue => {
      return this.mapservice.retrieveAddress(aValue.startPosition.coordinates[1], aValue.startPosition.coordinates[0], 250);
    });
    return Promise.all(reverseGeocodeCalls)
      .catch(() => reverseGeocodeCalls)
      .then(results => {
        results.forEach(result => {
          addresses.push(result);
        });
        var enrichedWithAddresses = rpms.rpms.map((value, index) => {
          return { ...value, address: addresses[index], startPosition: null, endPosition: null };
        });

        var merged = { summary: rpms.summary, rpms: enrichedWithAddresses };
        return merged;
      });
  };

  setDefaultDate = () => {
    this.setState({
      startDate: moment()
        .subtract(6, 'days')
        .startOf('day')
        .toDate(),
      endDate: moment()
        .startOf('day')
        .toDate()
    });
  };

  filterChanged = (startDate, endDate) => {
    this.setState({ startDate, endDate });
  };

  filterChangedGraph = (startDateGraph, endDateGraph) => {
    if (!isEqual(startDateGraph, this.state.startDateGraph)) {
      if (
        moment().isBefore(startDateGraph) ||
        moment()
          .add(7, 'days')
          .isBefore(startDateGraph)
      ) {
        this.setDefaultDate();
      } else {
        this.setState({
          startDateGraph: startDateGraph,
          endDateGraph: moment(startDateGraph)
            .add(6, 'days')
            .startOf('day')
            .toDate()
        });
      }
    } else if (!isEqual(endDateGraph, this.state.endDateGraph)) {
      this.setState({
        startDateGraph: moment(endDateGraph)
          .subtract(6, 'days')
          .startOf('day')
          .toDate(),
        endDateGraph: endDateGraph
      });
    } else {
      this.setState({ startDateGraph, endDateGraph });
    }
  };

  render() {
    const { visible, onCancel, translationsStore } = this.props;
    const { startDateGraph, endDateGraph, startDate, endDate, rpms, rpmsGraph, overRpmThreshold } = this.state;

    return (
      <div>
        <AirTabbedModal
          className="RpmDataModal"
          style={{ height: 566 }}
          contentStyle={{ paddingBottom: '80px' }}
          color="var(--dark-grey)"
          tabs={[
            {
              key: 'list',
              title: translationsStore.translate('quickFocus.rpm.views.list'),
              render: (
                <div className="RpmDataModal">
                  <RpmModalHeader startDate={startDate} endDate={endDate} onChange={this.filterChanged} rpms={rpms} overRpmThreshold={overRpmThreshold} />
                  <RpmModalSummary summary={rpms.summary} />
                  <RpmDataTable rpms={rpms.rpms} />
                </div>
              )
            },
            {
              key: 'graph',
              title: translationsStore.translate('quickFocus.rpm.views.map'),
              render: (
                <div className="rpmTabChart">
                  <RpmModalGraphHeader startDate={startDateGraph} endDate={endDateGraph} onChange={this.filterChangedGraph} rpms={rpms} overRpmThreshold={overRpmThreshold} />
                  <RpmTabChart onChange={this.filterChangedGraph} data={(rpmsGraph || {}).rpms} startDate={startDateGraph} endDate={endDateGraph} />
                </div>
              )
            }
          ]}
          visible={visible}
          width={896}
          onCancel={onCancel}
        />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/App/ClientListWithMap/ClientDetail/QuickFocus/RpmDataModal/RpmDataModal.js